/**
 * Ski Set Selector — "Make it a Set" widget
 *
 * Renders a hover-triggered popup card per set category (binding / skin).
 * Each row stays 45px collapsed; hovering opens a floating popup above it with
 * the product image, description, price, and a list of compatible alternatives.
 *
 * Mobile (<= 1024px) has no hover, so the floating popup never reveals — the
 * description was unreadable and the tap felt dead. There the trigger tap opens
 * a wishlist-style bottom-sheet (<info-drawer>) listing the options as readable
 * rows (thumb + title + description + price); picking one commits and closes.
 * The desktop hover/dropdown path is unchanged. AAS-4709
 *
 * Compatible bindings/skins come from the selected variant's
 * pim.ecom_bundle_bindings / pim.ecom_bundle_skins (list.variant_reference —
 * each entry IS the purchasable variant, so its id is what goes in the cart).
 * Their details (title, price, image, handle, variant id, availability) are
 * rendered into the data-compat blob directly by Liquid, so resolving a set
 * needs NO network call. The Storefront API is a fallback only: products Liquid
 * could not seed, and a last-resort variant lookup at add-to-cart.
 * Bindings and skins are independent lists (no binding→skin filtering).
 *
 * Options are filtered to what is actually purchasable and sorted
 * deterministically before render — see _presentableIds(). Both the desktop
 * dropdown and the mobile modal go through getBindingIds()/getSkinIds(), so
 * that single seam covers both. AAS-4705 / AAS-4706
 *
 * Wrapped in an IIFE + load guard because the snippet renders twice
 * (desktop + mobile), so this file is included on the page more than once.
 */

(function () {
  if (window.__skiSetSelectorLoaded) return;
  window.__skiSetSelectorLoaded = true;

const esc = (str) =>
  String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');

// AAS-5059: GA4 payload for an info link, as a ready-to-inline attribute.
// The payload itself is produced by snippets/datalayer-gtm-item.liquid and
// arrives on each catalog entry as `gtmItem` (seeded via
// snippets/ski-set-product-json.liquid) — the field logic is never rebuilt
// here. Returns '' when the seed carried none, so the anchor simply stays
// untracked rather than emitting a malformed attribute that
// assets/datalayer-tracking.js would fail to parse.
const gtmItemAttr = (product) => {
  if (!product || !product.gtmItem) return '';
  try {
    return ` data-gtm-item="${esc(JSON.stringify(product.gtmItem))}"`;
  } catch (e) {
    return '';
  }
};

// One MediaQueryList for the module — constructing it per pointer event
// (hover preview, card close) allocates needlessly.
const REDUCE_MOTION_MQ = window.matchMedia('(prefers-reduced-motion: reduce)');

// Below this width the hover popup can't be reached (no pointer hover), so the
// trigger opens a bottom-sheet modal instead. Matches the component's own
// mobile breakpoint (see the @media block in ski-set-selector.css). AAS-4709
const MOBILE_MQ = window.matchMedia('(max-width: 1024px)');

// AAS-4705. Option order used to be inherited straight from the PIM metafield
// list, which is not stable across markets (references the market cannot see are
// dropped, and the list order itself is not guaranteed) — the same ski showed
// Shift 10 / 13 / 16 in one market and 10 / 16 / 13 in another. Sort by title
// instead. `numeric` is what makes "Shift 9" precede "Shift 10"; plain
// lexicographic ordering puts 10 before 9.
const TITLE_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

// Per-instance drawer id counter — the snippet can render twice (desktop +
// mobile copies share a ski id), so ids must not collide.
let skiSetModalSeq = 0;

class SkiSetSelectorElement extends HTMLElement {
  connectedCallback() {
    this.skiId = this.dataset.skiId;
    if (!this.skiId) return;

    this.variantCache = {};
    this.selectedSize = null;
    this.selectedBinding = null;
    this.selectedSkin = null;
    this.loading = true;
    this.fetchError = false;

    this.uiState = {
      binding: { cardOpen: false },
      skin:    { cardOpen: false },
    };

    this.loadData();
    this.prefetchProducts();
    this.bindEvents();
    this.render();
  }

  /* ── Data ── */

  getStorefrontConfig() {
    const domain = this.dataset.shopDomain || 'armadasnow-qual.myshopify.com';
    return {
      endpoint: `https://${domain}/api/2026-01/graphql.json`,
      token: this.dataset.storefrontToken,
    };
  }

  // The market's country, as a Storefront CountryCode. Empty when unknown, which
  // callers must read as "send no @inContext" rather than guessing a country.
  _countryCode() {
    const code = window.theme?.market?.country_code || window.Shopify?.country || '';
    return /^[A-Za-z]{2}$/.test(code) ? code.toUpperCase() : '';
  }

  _productsQuery(inContext) {
    const args = inContext ? '($ids: [ID!]!, $country: CountryCode)' : '($ids: [ID!]!)';
    const ctx  = inContext ? ' @inContext(country: $country)' : '';
    return `
      query GetProductsByIds${args}${ctx} {
        nodes(ids: $ids) {
          ... on Product {
            id title handle description
            claim: metafield(namespace: "pim", key: "claim") { value }
            featuredImage { url }
            variants(first: 30) {
              nodes {
                id title availableForSale
                price { amount currencyCode }
                selectedOptions { name value }
              }
            }
          }
        }
      }
    `;
  }

  loadData() {
    const compatEl = this.querySelector('[data-compat]');
    this.compat = compatEl ? JSON.parse(compatEl.textContent) : { variants: {} };
    this.catalog = {
      skis: { [this.skiId]: this.compat.ski || {} },
      bindings: {},
      skins: {},
    };
    this._seedCatalogFromLiquid();
  }

  // PIM bindings/skins arrive as full product objects rendered by Liquid; seed the
  // catalog + variant cache from them so no Storefront fetch is needed for the PIM path.
  _seedCatalogFromLiquid() {
    const seed = (type, p) => {
      if (!p || typeof p !== 'object' || p.id == null) return;
      const id = String(p.id);
      this.catalog[type][id] = {
        id: p.id,
        // The catalog is keyed by the REFERENCE id, which for a variant
        // reference is the variant id — so this is the only place the parent
        // product id survives. _binding_product_id / _skin_product_id used to
        // send the variant id under a product-id name.
        productId: p.product_id ?? null,
        handle: p.handle || null,
        title: p.title || '',
        description: p.description || '',
        price: p.price || 0,
        image: p.image || null,
        // AAS-4706: was hardcoded true, so out-of-stock bindings stayed
        // selectable and only failed at the cart. Liquid now sends the real
        // (inventory- and market-aware) value. Treat a MISSING flag as available
        // so the Storefront fallback path, which cannot know yet, is unchanged.
        available: p.available !== false,
        // AAS-5059: GA4 item payload for this product's info link. Rendered by
        // Liquid (datalayer-gtm-item) into the seed — see gtmItemAttr().
        gtmItem: p.gtm_item || null,
      };
      if (p.variant_id != null) {
        this.variantCache[id] = [{
          id: p.variant_id, title: p.title || '',
          availableForSale: p.available !== false, option1: null, option2: null,
        }];
      }
    };

    Object.values(this.compat.variants || {}).forEach(set => {
      if (!set) return;
      (Array.isArray(set.bindings) ? set.bindings : []).forEach(p => seed('bindings', p));
      (Array.isArray(set.skins) ? set.skins : []).forEach(p => seed('skins', p));
    });

    // Seed the ski's own variants (we are on its PDP) for size→variant mapping at add-to-cart.
    // `price` / `compare_at_price` come along so the set total can be built from
    // the SELECTED size rather than the product's cheapest variant. AAS-4710
    if (Array.isArray(this.compat.ski?.variants)) {
      this.variantCache[this.skiId] = this.compat.ski.variants.map(v => ({
        id: v.id, title: v.title, availableForSale: !!v.available,
        price: v.price ?? null,
        compareAtPrice: v.compare_at_price || 0,
        option1: v.option1 || null, option2: v.option2 || null,
      }));
    }
  }

  prefetchProducts() {
    const bindingIds = new Set();
    const skinIds    = new Set();
    Object.values(this.compat.variants || {}).forEach(set => {
      const eff = this._effectiveIds(set);
      eff.bindings.forEach(id => bindingIds.add(id));
      eff.skins.forEach(id => skinIds.add(id));
    });

    // Only fetch what Liquid could NOT already provide: the ski itself if its
    // variants weren't seeded, plus any accessory that arrived as a bare id
    // rather than a full product object. PIM products are already in the catalog
    // from _seedCatalogFromLiquid(), so normally this fetches nothing.
    const entries = [];
    if (!this.variantCache[this.skiId]) entries.push({ id: this.skiId, type: 'ski' });
    bindingIds.forEach(id => { if (!this.catalog.bindings[id]) entries.push({ id, type: 'bindings' }); });
    skinIds.forEach(id => { if (!this.catalog.skins[id]) entries.push({ id, type: 'skins' }); });

    // Everything resolved from Liquid (PIM) — no Storefront round-trip needed.
    if (entries.length === 0) {
      this.loading = false;
      this.render();
      return;
    }

    const { endpoint, token } = this.getStorefrontConfig();
    const gids = entries.map(({ id }) => `gid://shopify/Product/${id}`);

    if (!token) {
      console.error('[ski-set] Missing Storefront access token (shop.metafields.custom.storefront_access_token) — needed to resolve products Liquid could not seed.');
      this.loading = false;
      this.fetchError = true;
      this.render();
      return;
    }

    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        // @inContext(country:) is REQUIRED for the prices here to mean the same
        // thing as the ones Liquid seeds. Without it the Storefront API answers in
        // the token's default context — i.e. SHOP currency — while the ski line and
        // the on-screen total are in market currency. That silently mixed
        // currencies inside a single set (no market switch needed) and made
        // `_price_currency` unable to describe the payload honestly. `availableForSale`
        // is market-scoped too, so this also stops a product unavailable in the
        // shopper's market from rendering as selectable on this path.
        // Guarded: an absent/invalid country code would error the whole query, so
        // fall back to the un-contexted form rather than breaking the fallback path.
        query: this._productsQuery(!!this._countryCode()),
        variables: {
          ids: gids,
          ...(this._countryCode() && { country: this._countryCode() }),
        },
      }),
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Storefront API HTTP ${r.status}`);
        const json = await r.json();
        if (json.errors) throw new Error('Storefront API errors: ' + JSON.stringify(json.errors));
        if (!json?.data?.nodes) throw new Error('Storefront API returned no product nodes');
        return json;
      })
      .then(json => {
        const mapVariant = v => ({
          id: parseInt(v.id.split('/').pop(), 10),
          title: v.title,
          availableForSale: v.availableForSale,
          price: Math.round(parseFloat(v.price.amount) * 100),
          option1: v.selectedOptions[0]?.value || null,
          option2: v.selectedOptions[1]?.value || null,
        });

        json.data.nodes.forEach((p, i) => {
          if (!p) return;
          const { id: productId, type } = entries[i];
          const variants = p.variants.nodes.map(mapVariant);

          if (type === 'ski') {
            this.variantCache[this.skiId] = variants;
          } else {
            this.catalog[type][productId] = {
              id: productId,
              // This path fetched BY product id, so the key already is one.
              productId,
              handle: p.handle,
              title: p.title,
              // AAS-4709: PIM claim is the accessory blurb; product description
              // is the DEPRECATED legacy fallback (until 100% PIM coverage).
              description: p.claim?.value || p.description || '',
              price: variants[0]?.price || 0,
              image: p.featuredImage?.url || null,
              // AAS-4706: purchasable if ANY variant is — this path resolves a
              // whole product, not the single variant the PIM list points at.
              available: variants.some(v => v.availableForSale),
            };
            this.variantCache[productId] = variants;
          }
        });

        this.loading = false;
        this.render();
      })
      .catch((err) => {
        console.error('[ski-set] Failed to load binding/skin products:', err);
        this.loading = false;
        this.fetchError = true;
        this.render();
      });
  }

  getVariantId(productId) {
    return this.variantCache[productId]?.[0]?.id ?? null;
  }

  // The ski variant the shopper actually configured. `selectedSize` is the
  // variant title (setSize is called with variant.title); the option match is a
  // fallback for single-option products, where the title is the option value.
  getSelectedSkiVariant() {
    if (!this.selectedSize) return null;
    const variants = this.variantCache[this.skiId] || [];
    return variants.find(v => v.title === this.selectedSize)
      || variants.find(v => v.option1 === this.selectedSize || v.option2 === this.selectedSize)
      || null;
  }

  async fetchVariantsById(productId) {
    const { endpoint, token } = this.getStorefrontConfig();
    const country = this._countryCode();
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      // Same market-context requirement as _productsQuery: `availableForSale` is
      // market-scoped, so an un-contexted read can offer a size the shopper's
      // market cannot actually sell. This path feeds size→variant resolution at
      // add-to-cart, so a wrong answer here fails at the cart, not on screen.
      body: JSON.stringify({
        query: `query GetVariants($id: ID!${country ? ', $country: CountryCode' : ''})${country ? ' @inContext(country: $country)' : ''} {
          node(id: $id) {
            ... on Product {
              variants(first: 30) {
                nodes {
                  id title availableForSale
                  price { amount }
                  selectedOptions { name value }
                }
              }
            }
          }
        }`,
        variables: {
          id: `gid://shopify/Product/${productId}`,
          ...(country && { country }),
        },
      }),
    });
    const json = await res.json();
    return (json?.data?.node?.variants?.nodes || []).map(v => ({
      id: parseInt(v.id.split('/').pop(), 10),
      title: v.title,
      availableForSale: v.availableForSale,
      option1: v.selectedOptions[0]?.value || null,
      option2: v.selectedOptions[1]?.value || null,
    }));
  }

  resolve(type, id) {
    return this.catalog?.[type]?.[id] ?? null;
  }

  formatPrice(cents) {
    // AAS-4164 catalog-only market — must return '' BEFORE the fallback below.
    // marketMoney.format() returns '' on this market, which this method would
    // otherwise read as "formatting failed" and answer with the hardcoded
    // Intl.NumberFormat fallback — rendering a real price on a market that must
    // not show one. The empty string here is a valid result, not a failure.
    if (document.documentElement.classList.contains('market--catalog')) return '';

    const formattedPrice = window.theme?.marketMoney?.format(cents);

    if (formattedPrice) {
      return window.theme.marketMoney.stripHtml(formattedPrice);
    }

    return new Intl.NumberFormat(window.Shopify?.locale || 'en-GB', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'GBP',
    }).format(cents / 100);
  }

  // AAS-4707: crypto.randomUUID() only exists in a secure context, so on a
  // non-https preview or a proxied theme-editor URL this threw — and it runs
  // inside getCartProperties(), i.e. on the add-to-cart path, taking the whole
  // submit down with it. Fall back rather than break the sale.
  generateSetLinkId() {
    const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().substring(0, 8)
      : Math.random().toString(36).substring(2, 10);
    return 'SET-' + uuid.toUpperCase();
  }

  stripHtml(html) {
    const el = document.createElement('div');
    el.innerHTML = html;
    return (el.textContent || el.innerText || '').trim();
  }

  /* ── Size sync ── */

  setSize(size) {
    // Cancel in-flight close animations: their delayed commits would
    // re-render + dispatch a stale ski-set:change against the state this
    // reset is about to replace.
    if (this._closeTimers) {
      Object.keys(this._closeTimers).forEach(t => {
        clearTimeout(this._closeTimers[t].id);
        delete this._closeTimers[t];
      });
    }
    this.selectedSize = size;
    this.selectedBinding = null;
    this.selectedSkin = null;
    Object.keys(this.uiState).forEach(type => { this.uiState[type].cardOpen = false; });
    this.render();
  }

  /* ── Compatible bindings / skins ──
     Each variant entry is { bindings: [product…], skins: [product…] }, sourced
     from pim.ecom_bundle_bindings / pim.ecom_bundle_skins. Bindings and skins
     are independent lists. */

  _effectiveIds(set) {
    if (!set) return { bindings: [], skins: [] };
    // PIM entries are full product objects ({ id, ... }).
    const pimIds = (arr) => [...new Set((Array.isArray(arr) ? arr : [])
      .map(x => (x && typeof x === 'object') ? x.id : x)
      .filter(v => v != null)
      .map(String))];
    return { bindings: pimIds(set.bindings), skins: pimIds(set.skins) };
  }

  getVariantSets() {
    if (!this.selectedSize) return { bindings: [], skins: [] };
    return this._effectiveIds(this.compat.variants?.[this.selectedSize]);
  }

  // Unknown availability counts as available: only the Liquid-seeded path knows
  // the real value, and hiding options the Storefront fallback has not resolved
  // yet would blank the widget on a slow network.
  _isAvailable(type, id) {
    const p = this.resolve(type, id);
    return !p || p.available !== false;
  }

  // The single seam every render path shares — desktop dropdown, mobile modal
  // and the row-visibility checks all read these ids.
  //
  // AAS-4706: drop what cannot be bought. The out-of-stock combination used to
  // stay selectable and the shopper only discovered the problem from a cart
  // error. Per the ticket these are REMOVED rather than shown disabled; to show
  // them greyed instead, stop filtering here and pass _isAvailable() through to
  // renderCategoryRow / renderModalOptions as a per-row flag.
  //
  // AAS-4705: then order deterministically, so every market renders the same
  // sequence regardless of how PIM happened to store the list.
  _presentableIds(type, ids) {
    return [...new Set(ids)]
      .filter(id => this._isAvailable(type, id))
      .sort((a, b) => TITLE_COLLATOR.compare(
        this.resolve(type, a)?.title || '',
        this.resolve(type, b)?.title || '',
      ));
  }

  getBindingIds() {
    return this._presentableIds('bindings', this.getVariantSets().bindings);
  }

  getSkinIds() {
    return this._presentableIds('skins', this.getVariantSets().skins);
  }

  /* ── Card open / close ── */

  openCard(type) {
    if (!this.selectedSize) return;
    const ids = type === 'binding' ? this.getBindingIds() : this.getSkinIds();
    if (ids.length === 0) return;

    // Flush any in-flight close animations (running their commit now) so a
    // delayed render can't fire after this open and clobber the fresh state,
    // and so a pending selection commit still dispatches its change event.
    if (this._closeTimers) {
      Object.keys(this._closeTimers).forEach(t => {
        const pending = this._closeTimers[t];
        clearTimeout(pending.id);
        delete this._closeTimers[t];
        pending.commit();
      });
    }

    Object.keys(this.uiState).forEach(t => {
      this.uiState[t].cardOpen = t === type;
    });
    this.render();
  }

  // Plays the close animation (popups collapse, chevron rotates back) on the
  // still-open card, then runs `commit` (which must flip state + render).
  // Commits immediately when the node is missing or motion is reduced.
  _animatedClose(type, commit) {
    const card = this.querySelector(`.ski-set__card--${type}.ski-set__card--open`);
    const reduceMotion = REDUCE_MOTION_MQ.matches;
    if (!card || reduceMotion) {
      commit();
      return;
    }
    this._closeTimers = this._closeTimers || {};
    card.classList.add('ski-set__card--closing');
    const id = setTimeout(() => {
      delete this._closeTimers[type];
      commit();
    }, 230);
    this._closeTimers[type] = { id, commit };
  }

  closeCard(type) {
    // Already animating shut — let the pending commit finish.
    if (this._closeTimers?.[type]) return;
    this._animatedClose(type, () => {
      this.uiState[type].cardOpen = false;
      this.render();
    });
  }

  selectFromDropdown(type, id) {
    const currentSel = type === 'binding' ? this.selectedBinding : this.selectedSkin;

    if (currentSel?._id === id) {
      if (type === 'binding') this.selectedBinding = null;
      else this.selectedSkin = null;
    } else {
      const p = this.resolve(`${type}s`, id);
      if (!p) return;
      if (type === 'binding') this.selectedBinding = { _id: id, ...p };
      else this.selectedSkin = { _id: id, ...p };
    }

    const commit = () => {
      this.uiState[type].cardOpen = false;
      this.render();
      this.dispatchChange();
    };

    // Selecting while a close is already animating (e.g. clicked during the
    // hover-out fade): replace the pending plain close with this commit so
    // the change event isn't lost.
    if (this._closeTimers?.[type]) {
      clearTimeout(this._closeTimers[type].id);
      delete this._closeTimers[type];
      commit();
      return;
    }

    this._animatedClose(type, commit);
  }

  /* ── Mobile modal (wishlist-style bottom sheet) ─────────────────────────
     On touch there is no hover, so the floating description popup never
     reveals. Instead the trigger tap opens a shared <info-drawer> bottom
     sheet (the same shell the wishlist modal uses) that lists every option as
     a readable row. Selecting commits through the normal selectFromDropdown /
     remove paths, then closes the sheet. AAS-4709 */

  // Lazily build the drawer on first open (by then info-drawer.js has upgraded
  // the element). One drawer per selector instance, appended to <body> so it
  // escapes the PDP's stacking/overflow contexts like every other info-drawer.
  _ensureModal() {
    if (this._modal) return this._modal;

    const drawer = document.createElement('info-drawer');
    drawer.className = 'info-drawer ski-set-modal';
    drawer.id = `ski-set-modal-${this.skiId}-${++skiSetModalSeq}`;
    const titleId = `${drawer.id}-title`;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-labelledby', titleId);
    drawer.setAttribute('tabindex', '-1');
    // AAS-5059: the sheet is appended to <body>, so it sits OUTSIDE
    // .ski-set-selector-wrapper and cannot inherit its list root. Declare the
    // same list identity here (snippets/ski-set.liquid) or every option's info
    // link would fall back to the page-level bucket and report under the PDP's
    // own name. Keep the two values in sync with that snippet.
    drawer.setAttribute('data-list-id', 'ski-set-compatible');
    drawer.setAttribute('data-list-name', 'Ski set — compatible accessories');
    // Built before append so info-drawer's connectedCallback finds the panel.
    // Close markup comes from <template data-ski-set-close-button> (close-button.liquid).
    drawer.innerHTML = `
      <div class="info-drawer__panel ski-set-modal__panel">
        <header class="info-drawer__header ski-set-modal__header">
          <h2 id="${titleId}" class="info-drawer__title ski-set-modal__title" data-ski-modal-title></h2>
          ${this.renderCloseButton()}
        </header>
        <div class="ski-set-modal__body" data-ski-modal-body></div>
      </div>`;

    // Selection is delegated here (the drawer lives outside <ski-set-selector>,
    // so the element's own click handler can't see these clicks).
    drawer.addEventListener('click', (e) => {
      // AAS-5075: the option row itself now carries data-action (whole-tile
      // select), so the info icon — a real <a> nested inside it — has to opt
      // out explicitly or a tap would select AND navigate.
      if (e.target.closest('a[href]')) return;
      const opt = e.target.closest('[data-action]');
      if (!opt || !drawer.contains(opt)) return;
      const { action, type, id } = opt.dataset;
      if (action === 'select-from-dropdown') {
        this.selectFromDropdown(type, id);
      } else if (action === 'remove-from-dropdown') {
        if (type === 'binding') this.selectedBinding = null;
        else this.selectedSkin = null;
        this.uiState[type].cardOpen = false;
        this.render();
        this.dispatchChange();
      }
      drawer.close();
    });

    document.body.appendChild(drawer);
    this._modal = drawer;
    return drawer;
  }

  openCardModal(type, trigger) {
    if (!this.selectedSize) return;
    const ids = type === 'binding' ? this.getBindingIds() : this.getSkinIds();
    if (ids.length === 0) return;

    const drawer = this._ensureModal();
    if (typeof drawer.open !== 'function') return; // info-drawer not upgraded yet

    const selected = type === 'binding' ? this.selectedBinding : this.selectedSkin;
    const label = type === 'binding'
      ? (window.skiSetStrings?.addBinding || 'Add a binding')
      : (window.skiSetStrings?.addSkins || 'Add skins');

    drawer.querySelector('[data-ski-modal-title]').textContent = label;
    drawer.querySelector('[data-ski-modal-body]').innerHTML = this.renderModalOptions(type, ids, selected);
    drawer.open(trigger);
  }

  renderModalOptions(type, ids, selected) {
    const selectLabel = window.skiSetStrings?.select || 'Select';
    const removeLabel = type === 'binding'
      ? (window.skiSetStrings?.removeBinding || 'Remove binding')
      : (window.skiSetStrings?.removeSkin || 'Remove skin');

    const rows = ids.map((id) => {
      const p = this.resolve(`${type}s`, id);
      if (!p) return '';
      const isSel = selected?._id === id;
      const desc  = esc(this.stripHtml(p.description || ''));
      const price = esc(this.formatPrice(p.price));
      const thumb = p.image
        ? `<img class="ski-set-modal__thumb" src="${esc(p.image)}" alt="${esc(p.title)}" loading="lazy">`
        : '';
      // Info icon (links to the product page) — the same control as the desktop
      // flyout, sat to the right of the title.
      const infoLabel = `${window.skiSetStrings?.moreInfo || 'More info'} — ${p.title}`;
      const infoBtn = p.handle
        ? `<a class="ski-set__info-btn ski-set-modal__info-btn"
              href="/products/${esc(p.handle)}"${gtmItemAttr(p)}
              aria-label="${esc(infoLabel)}">${this.renderInfoIcon()}</a>`
        : '';
      // Wishlist "add to cart"-style CTA: Select adds this product to the set;
      // once selected it flips to Remove, so the toggle lives entirely on the
      // card (no separate remove row).
      const cta = isSel
        ? `<button type="button" class="ski-set-modal__cta ski-set-modal__cta--remove"
                   data-action="remove-from-dropdown" data-type="${type}">${esc(removeLabel)}</button>`
        : `<button type="button" class="ski-set-modal__cta"
                   data-action="select-from-dropdown" data-type="${type}" data-id="${esc(String(id))}"
                   aria-label="${esc(selectLabel)} ${esc(p.title)}">${esc(selectLabel)}</button>`;
      // AAS-5075: on touch the whole tile is the select hitbox — the row repeats
      // the CTA's data-action so the delegated handler above picks it up. Only
      // unselected rows are tappable: one binding/skin can be chosen at a time
      // (radio semantics), and a stray tap on the chosen one must not drop it —
      // removing stays an explicit press on the Remove CTA.
      const rowAction = isSel
        ? ''
        : ` data-action="select-from-dropdown" data-type="${type}" data-id="${esc(String(id))}"`;
      return `
        <div class="ski-set-modal__option${isSel ? ' ski-set-modal__option--selected' : ''}"${rowAction}>
          <span class="ski-set-modal__thumb-wrap"${!p.image ? ' hidden' : ''}>${thumb}</span>
          <span class="ski-set-modal__option-info">
            <span class="ski-set-modal__title-row">
              <span class="ski-set-modal__option-title">${esc(p.title)}</span>
              ${infoBtn}
            </span>
            ${desc ? `<span class="ski-set-modal__option-desc">${desc}</span>` : ''}
            <span class="ski-set-modal__option-price">${price}</span>
            <span class="ski-set-modal__cta-row">${cta}</span>
          </span>
        </div>`;
    }).join('');

    return `<div class="ski-set-modal__options" role="group">${rows}</div>`;
  }

  renderCloseButton() {
    return this.querySelector('[data-ski-set-close-button]')?.innerHTML || '';
  }

  /* ── Hover preview — direct DOM update, no re-render ── */

  // Animated swap: current image/text nudge up + fade out, the new content
  // comes in from below. Rapid row-hovers retarget the pending data instead
  // of stacking animations.
  _updateCardPreview(item, data) {
    const body = item.querySelector('.ski-set__body');
    const key  = data.id || data.productHandle || data.productTitle || '';
    if (!body) {
      this._applyPreview(item, data);
      return;
    }
    if (body.dataset.previewKey === key) return;
    body.dataset.previewKey = key;

    if (REDUCE_MOTION_MQ.matches) {
      this._applyPreview(item, data);
      return;
    }

    this._pendingPreview = { item, data };
    if (this._previewSwapTimer) return; // out-phase running; it applies the latest

    body.classList.add('is-preview-out');
    this._previewSwapTimer = setTimeout(() => {
      this._previewSwapTimer = null;
      const pending = this._pendingPreview;
      if (!pending) return;
      this._applyPreview(pending.item, pending.data);
      body.classList.remove('is-preview-out');
      body.classList.add('is-preview-in');
      void body.offsetWidth; // commit the below+hidden start state
      body.classList.remove('is-preview-in');
    }, 160);
  }

  _applyPreview(item, data) {
    const img     = item.querySelector('[data-card-image]');
    const desc    = item.querySelector('[data-card-desc]');
    const price   = item.querySelector('[data-card-price]');
    const infoBtn = item.querySelector('[data-card-info]');
    const wrap    = item.querySelector('[data-card-image-wrap]');

    if (img) {
      if (data.productImage) {
        img.src = data.productImage;
        img.alt = data.productTitle || '';
        if (wrap) wrap.hidden = false;
      } else if (wrap) {
        wrap.hidden = true;
      }
    }
    if (desc) desc.textContent = data.productDesc || '';
    if (price) price.textContent = data.productPrice || '';
    if (infoBtn?.tagName === 'A') {
      infoBtn.href = data.productHandle ? `/products/${data.productHandle}` : '#';
      // AAS-5059: href and GA4 payload must move together — see the render
      // comment on data-orig-gtm-item. A hovered row with no payload clears the
      // attribute rather than leaving the previous product's item behind.
      if (data.productGtmItem) {
        infoBtn.setAttribute('data-gtm-item', data.productGtmItem);
      } else {
        infoBtn.removeAttribute('data-gtm-item');
      }
    }
  }

  _restoreCardPreview(item) {
    // Instant: only runs once the pointer has left the options list, when the
    // panel is already fading out. Reset the key so the next hover animates.
    if (this._previewSwapTimer) {
      clearTimeout(this._previewSwapTimer);
      this._previewSwapTimer = null;
      this._pendingPreview = null;
    }
    const body = item.querySelector('.ski-set__body');
    if (body) {
      body.classList.remove('is-preview-out', 'is-preview-in');
      // Content goes back to the original product, so the key must follow —
      // clearing it made the next hover on that same product swap-blink.
      if (body.dataset.origKey) {
        body.dataset.previewKey = body.dataset.origKey;
      } else {
        delete body.dataset.previewKey;
      }
    }
    const img     = item.querySelector('[data-card-image]');
    const desc    = item.querySelector('[data-card-desc]');
    const price   = item.querySelector('[data-card-price]');
    const infoBtn = item.querySelector('[data-card-info]');
    const wrap    = item.querySelector('[data-card-image-wrap]');

    if (img) {
      img.src = img.dataset.origSrc || '';
      img.alt = img.dataset.origAlt || '';
      if (wrap) wrap.hidden = !img.dataset.origSrc;
    }
    if (desc) desc.textContent = desc.dataset.origText || '';
    if (price) price.textContent = price.dataset.origText || '';
    if (infoBtn?.tagName === 'A') {
      infoBtn.href = infoBtn.dataset.origHref || '#';
      // Payload follows href back to the card's own product (AAS-5059).
      if (infoBtn.dataset.origGtmItem) {
        infoBtn.setAttribute('data-gtm-item', infoBtn.dataset.origGtmItem);
      } else {
        infoBtn.removeAttribute('data-gtm-item');
      }
    }
  }

  /* ── Events ── */

  bindEvents() {
    this.addEventListener('click', (e) => {
      // Toggle popup on trigger click
      const trigger = e.target.closest('.ski-set__trigger');
      if (trigger) {
        const wrap = trigger.closest('.ski-set__row[data-type]');
        if (wrap) {
          const { type } = wrap.dataset;
          // Mobile: open the bottom-sheet modal instead of the hover dropdown.
          if (MOBILE_MQ.matches) {
            this.openCardModal(type, trigger);
            return;
          }
          if (this.uiState[type]?.cardOpen) {
            this.closeCard(type);
          } else {
            this.openCard(type);
          }
          return;
        }
      }

      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const { action, type, id } = btn.dataset;

      if (action === 'select-from-dropdown') {
        this.selectFromDropdown(type, id);
      } else if (action === 'remove-from-dropdown') {
        if (type === 'binding') this.selectedBinding = null;
        else this.selectedSkin = null;
        const commit = () => {
          this.uiState[type].cardOpen = false;
          this.render();
          this.dispatchChange();
        };
        if (this._closeTimers?.[type]) {
          clearTimeout(this._closeTimers[type].id);
          delete this._closeTimers[type];
          commit();
        } else {
          this._animatedClose(type, commit);
        }
      }
    });

    // Close all open popups when clicking outside the component.
    // composedPath() captures the original event path before any DOM re-render,
    // so this is reliable even when openCard() re-renders the DOM on the same click.
    document.addEventListener('click', (e) => {
      if (!e.composedPath().includes(this)) {
        Object.keys(this.uiState).forEach(type => {
          if (this.uiState[type].cardOpen) this.closeCard(type);
        });
      }
    });

    // Preview the hovered option (description, price, image). Fire for any product
    // option — NOT just ones with an image — so description/price still update when a
    // binding/skin has no featured image. _updateCardPreview hides the image when absent.
    this.addEventListener('pointerover', (e) => {
      const option = e.target.closest('.ski-set__option');
      if (option?.dataset.action === 'select-from-dropdown') {
        const item = option.closest('.ski-set__card');
        if (item) {
          this._updateCardPreview(item, option.dataset);
          // The body popup stays hidden until an option row is hovered.
          item.querySelector('.ski-set__body-popup')?.classList.add('is-visible');
        }
      }
    });

    this.addEventListener('pointerout', (e) => {
      // Restore the original preview only when the pointer leaves the options
      // list — moving between rows animates row-to-row instead of flashing
      // the original in between.
      const option = e.target.closest('.ski-set__option');
      if (
        option &&
        !option.contains(e.relatedTarget) &&
        !e.relatedTarget?.closest?.('.ski-set__popup')
      ) {
        const item = option.closest('.ski-set__card');
        if (item) this._restoreCardPreview(item);
      }

      // Hide the body popup when the pointer leaves the options list entirely
      // (moving between rows keeps it up).
      const popup = e.target.closest('.ski-set__popup');
      if (popup && !popup.contains(e.relatedTarget)) {
        popup.closest('.ski-set__card')
          ?.querySelector('.ski-set__body-popup')
          ?.classList.remove('is-visible');
      }

      // Close the open dropdown when the pointer leaves it entirely (opening
      // stays click-only; touch keeps the outside-click close instead).
      const openCard = e.target.closest('.ski-set__card--open');
      if (
        openCard &&
        e.pointerType !== 'touch' &&
        !openCard.contains(e.relatedTarget)
      ) {
        const row = openCard.closest('.ski-set__row[data-type]');
        const type = row?.dataset.type;
        if (type && this.uiState[type]?.cardOpen) this.closeCard(type);
      }
    });

    this.addEventListener('keydown', (e) => {
      // Escape dismisses an open dropdown (the only non-pointer close path).
      if (e.key === 'Escape') {
        Object.keys(this.uiState).forEach(type => {
          if (this.uiState[type].cardOpen) this.closeCard(type);
        });
        return;
      }
      if (e.key !== 'Enter' && e.key !== ' ') return;
      // Triggers carry no [data-action]; the click handler owns the toggle.
      const target = e.target.closest('[data-action], .ski-set__trigger');
      if (!target) return;
      e.preventDefault();
      target.click();
    });

    // Keyboard parity with the hover preview: focusing an option (options
    // are tabbable) reveals + retargets the detail panel like pointerover.
    this.addEventListener('focusin', (e) => {
      const option = e.target.closest('.ski-set__option');
      if (option?.dataset.action === 'select-from-dropdown') {
        const item = option.closest('.ski-set__card');
        if (item) {
          this._updateCardPreview(item, option.dataset);
          item.querySelector('.ski-set__body-popup')?.classList.add('is-visible');
        }
      }
    });
  }

  dispatchChange() {
    this.dispatchEvent(new CustomEvent('ski-set:change', {
      bubbles: true,
      detail: this.getSelection(),
    }));
  }

  /* ── Public API ── */

  getSelection() {
    return {
      skiId: this.skiId,
      size: this.selectedSize,
      binding: this.selectedBinding ? { id: this.selectedBinding._id, title: this.selectedBinding.title, price: this.selectedBinding.price } : null,
      skin:    this.selectedSkin    ? { id: this.selectedSkin._id,    title: this.selectedSkin.title,    price: this.selectedSkin.price    } : null,
      mountingFormType: this.compat.mounting_form_type || 'freeride',
    };
  }

  getCartProperties() {
    if (!this.selectedBinding && !this.selectedSkin) return null;

    const sel = this.getSelection();
    const setLinkId = this.generateSetLinkId();
    const ski = this.resolve('skis', this.skiId) || {};

    const nameParts = [ski.title || ''];
    if (sel.binding) nameParts.push(sel.binding.title);
    if (sel.skin)    nameParts.push(sel.skin.title);

    // AAS-4710: the ski's share of the total is the SELECTED size's price. This
    // read catalog.skis[…].price, which Liquid fills from `product.price` — the
    // product's CHEAPEST variant. On a ski priced per size the on-screen total
    // (built from the selected variant) and _set_total_price (built from the
    // cheapest) disagreed, so the Cart Transform repriced the line to a number
    // the shopper was never shown.
    const skiVariant = this.getSelectedSkiVariant();
    let totalCents = skiVariant?.price ?? ski.price ?? 0;
    if (sel.binding) totalCents += sel.binding.price;
    if (sel.skin)    totalCents += sel.skin.price;

    // AAS-XXXX: the currency `_set_total_price` / `_binding_price` / `_skin_price`
    // are denominated in. Every one of those is cents in the PRESENTMENT currency
    // of the market the shopper was in AT ADD-TO-CART (Liquid's product.price /
    // variant.price are market-converted), and nothing rewrites them when the
    // market changes — so a set added in CZ and checked out in DE hands the Cart
    // Transform 1300000 CZK-cents which it charges as EUR. Stamping the currency
    // lets the Function refuse to expand on a mismatch: the shopper sees one
    // un-expanded ski line — visibly broken and reported, rather than wrongly
    // charged. Omitted (not guessed) if the currency can't be read, so the
    // Function's check no-ops and behaviour is unchanged rather than every
    // market failing to expand.
    const priceCurrency =
      window.theme?.market?.currency_code || window.Shopify?.currency?.active || '';

    return {
      _set_display_name: nameParts.join(' + '),
      _set_total_price: (totalCents / 100).toFixed(2),
      ...(priceCurrency && { _price_currency: priceCurrency.toUpperCase() }),
      _GroupType: 'product set',
      _ProductSetType: sel.mountingFormType === 'touring' ? 'Touring set' : 'Alpine set',
      _SetLinkId: setLinkId,
      _ProductType: 'ski',
      _IsMainProduct: 'true',
      _mounting_form_type: sel.mountingFormType,
      ...(sel.binding && {
        _binding_product_id: this.resolve('bindings', sel.binding.id)?.productId || sel.binding.id,
        _binding_variant_id: String(this.getVariantId(sel.binding.id) || ''),
        _binding_title: sel.binding.title,
        _binding_price: String(sel.binding.price),
      }),
      ...(sel.skin && {
        _skin_product_id: this.resolve('skins', sel.skin.id)?.productId || sel.skin.id,
        _skin_variant_id: String(this.getVariantId(sel.skin.id) || ''),
        _skin_title: sel.skin.title,
        _skin_price: String(sel.skin.price),
      }),
    };
  }

  /* ── Helpers ── */

  hasSkins() {
    return Object.values(this.compat.variants || {}).some(set => this._effectiveIds(set).skins.length > 0);
  }

  /* ── Render ── */

  render() {
    const ui = this.querySelector('[data-ui]');
    if (!ui) return;

    if (this.loading) {
      ui.innerHTML = `<p class="ski-set__loading">${window.skiSetStrings?.loading || 'Loading…'}</p>`;
      return;
    }
    if (this.fetchError) {
      ui.innerHTML = `<p class="ski-set__error">${window.skiSetStrings?.errorLoading || 'Error loading options.'}</p>`;
      return;
    }

    const hasSize    = !!this.selectedSize;
    const bindingIds = hasSize ? this.getBindingIds() : [];
    const skinIds    = hasSize ? this.getSkinIds()    : [];

    const wrapper = this.closest('[data-wrapper-ski-id]');
    const hasCompatibleSets = bindingIds.length > 0 || skinIds.length > 0;

    if (hasSize && !hasCompatibleSets) {
      if (wrapper) wrapper.hidden = true;
      ui.innerHTML = '';
      return;
    }

    if (wrapper) wrapper.hidden = false;

    // Show the binding row unless a size is selected that has no bindings (and none picked) —
    // avoids a dead "Add a binding" row when a size only offers skins. Keep it visible with
    // no size selected so the "Select a size first" prompt still appears.
    const showBindings = !hasSize || bindingIds.length > 0 || !!this.selectedBinding;
    const showSkins    = !!this.selectedSkin || (this.hasSkins() && skinIds.length > 0);

    ui.innerHTML = `
      ${showBindings ? this.renderCategoryRow('binding', window.skiSetStrings?.addBinding || 'Add a binding', bindingIds, this.selectedBinding, hasSize) : ''}
      ${showSkins ? this.renderCategoryRow('skin',  window.skiSetStrings?.addSkins  || 'Add skins',  skinIds,   this.selectedSkin,  hasSize && skinIds.length > 0)  : ''}
    `;
  }

  renderCategoryRow(type, label, ids, selected, enabled) {
    const { cardOpen } = this.uiState[type];

    const categoryLabel = type === 'binding' ? (window.skiSetStrings?.bindingLabel || 'Binding')
                        : (window.skiSetStrings?.skinLabel || 'Skin');

    const wrapStart = `<div class="ski-set__row" data-type="${type}">
      <p class="ski-set__row-label">${esc(categoryLabel)}</p>`;
    const wrapEnd = `</div>`;

    if (!enabled) {
      return `${wrapStart}
        <div class="ski-set__card ski-set__card--${type}">
          <div class="ski-set__trigger">
            <span class="ski-set__trigger-label ski-set__trigger-label--error">${esc(window.skiSetStrings?.selectSizeFirst || 'Select a size first')}</span>
            ${this.renderChevron('left')}
          </div>
        </div>${wrapEnd}`;
    }

    if (ids.length === 0) {
      return `${wrapStart}
        <div class="ski-set__card ski-set__card--${type}">
          <div class="ski-set__trigger">
            <span class="ski-set__trigger-label">${esc(label)}</span>
            ${this.renderChevron('left')}
          </div>
        </div>${wrapEnd}`;
    }

    const previewId      = selected?._id || ids[0];
    const previewProduct = this.resolve(`${type}s`, previewId);

    if (!cardOpen) {
      return `${wrapStart}
        <div class="ski-set__card ski-set__card--${type}">
          <div class="ski-set__trigger${selected?.image ? ' ski-set__trigger--with-thumb' : ''}" role="button" tabindex="0" aria-expanded="false" aria-label="${esc(label)}">
            ${selected?.image ? `<img class="ski-set__trigger-thumb" src="${esc(selected.image)}" alt="${esc(selected.title)}" loading="lazy">` : ''}
            <span class="ski-set__trigger-label">${selected ? esc(selected.title) : esc(label)}</span>
            ${this.renderChevron('left')}
          </div>
        </div>${wrapEnd}`;
    }

    const origDesc  = previewProduct ? esc(this.stripHtml(previewProduct.description || '')) : '';
    const origPrice = previewProduct ? esc(this.formatPrice(previewProduct.price)) : '';
    const origHref  = previewProduct?.handle ? `/products/${esc(previewProduct.handle)}` : '#';

    // AAS-5059: the panel's info link retargets as the shopper hovers the option
    // rows, so its GA4 payload is swapped in lockstep with href (_applyPreview)
    // and restored from data-orig-gtm-item alongside data-orig-href
    // (_restoreCardPreview). Letting href move while the payload stayed put
    // would report the WRONG product on every previewed click.
    let origGtmAttr = '';
    if (previewProduct?.gtmItem) {
      try {
        origGtmAttr = ` data-orig-gtm-item="${esc(JSON.stringify(previewProduct.gtmItem))}"`;
      } catch (e) { origGtmAttr = ''; }
    }

    const infoBtn = previewProduct?.handle
      ? `<a class="ski-set__info-btn" data-card-info data-orig-href="${origHref}"${origGtmAttr} href="${origHref}"${gtmItemAttr(previewProduct)} aria-label="${esc(window.skiSetStrings?.moreInfo || 'More info')}">${this.renderInfoIcon()}</a>`
      : `<span class="ski-set__info-btn" data-card-info aria-hidden="true">${this.renderInfoIcon()}</span>`;

    const bodyHtml = previewProduct ? `
      ${'' /* preview-key pre-set to the rendered product: hovering the row
            that's already shown must NOT run the swap animation (it would
            blink identical content in and out — empty-state first hover).
            orig-key is immutable; restore resets preview-key to it. */}
      <div class="ski-set__body" data-preview-key="${esc(String(previewId))}" data-orig-key="${esc(String(previewId))}">
        <div class="ski-set__card-header">
          <span class="ski-set__badge">${esc(window.skiSetStrings?.compatible || 'Compatible')}</span>
          ${infoBtn}
        </div>
        <div class="ski-set__image-wrap" data-card-image-wrap${!previewProduct.image ? ' hidden' : ''}>
          <img class="ski-set__image"
               data-card-image
               data-orig-src="${esc(previewProduct.image || '')}"
               data-orig-alt="${esc(previewProduct.title)}"
               src="${esc(previewProduct.image || '')}"
               alt="${esc(previewProduct.title)}"
               loading="lazy">
        </div>
        <div class="ski-set__meta">
          <p class="ski-set__desc" data-card-desc data-orig-text="${origDesc}">${origDesc}</p>
          <p class="ski-set__price" data-card-price data-orig-text="${origPrice}">${origPrice}</p>
        </div>
      </div>` : `
      <div class="ski-set__body ski-set__body--loading">
        <p class="ski-set__loading">${window.skiSetStrings?.loading || 'Loading…'}</p>
      </div>`;

    const otherIds = selected ? ids.filter(id => id !== previewId) : [...ids];

    const removeLabel = type === 'binding' ? (window.skiSetStrings?.removeBinding || 'Remove binding')
                      : (window.skiSetStrings?.removeSkin || 'Remove skin');

    const removeRow = selected ? `
      ${otherIds.length > 0 ? '<div class="ski-set__option-divider"></div>' : ''}
      <div class="ski-set__option ski-set__option--remove"
           data-action="remove-from-dropdown" data-type="${type}"
           role="option" tabindex="0">
        <span class="ski-set__option-label">${esc(removeLabel)}</span>
      </div>` : '';

    const optionsHtml = (otherIds.length > 0 || selected) ? `
      <div class="ski-set__options" role="listbox">
        ${otherIds.map((id, index) => {
          const p = this.resolve(`${type}s`, id);
          if (!p) return '';
          const isSel = selected?._id === id;
          // AAS-5059: carried so hovering a row can retarget the panel's info
          // link AND its GA4 payload together (_applyPreview). The row itself is
          // a <div>, not a link, so it never emits select_item on its own.
          let rowGtm = '';
          if (p.gtmItem) {
            try { rowGtm = JSON.stringify(p.gtmItem); } catch (e) { rowGtm = ''; }
          }
          return `
            ${index > 0 ? '<div class="ski-set__option-divider"></div>' : ''}
            <div class="ski-set__option${isSel ? ' ski-set__option--selected' : ''}"
                 data-action="select-from-dropdown" data-type="${type}" data-id="${esc(String(id))}"
                 data-product-image="${esc(p.image || '')}"
                 data-product-title="${esc(p.title)}"
                 data-product-desc="${esc(this.stripHtml(p.description || ''))}"
                 data-product-price="${esc(this.formatPrice(p.price))}"
                 data-product-handle="${esc(p.handle || '')}"
                 data-product-gtm-item="${esc(rowGtm)}"
                 role="option" aria-selected="${isSel}" tabindex="0">
              <span class="ski-set__option-label">${esc(p.title)}</span>
            </div>`;
        }).join('')}
        ${removeRow}
      </div>` : '';

    return `${wrapStart}
      <div class="ski-set__card ski-set__card--${type} ski-set__card--open">
        ${'' /* With a selection, the detail panel opens along with the list
              (preview defaults to the selected product); otherwise it waits
              for the first option hover. */}
        <div class="ski-set__body-popup${selected ? ' is-visible' : ''}">
          ${bodyHtml}
        </div>
        <div class="ski-set__trigger${selected?.image ? ' ski-set__trigger--with-thumb' : ''}" role="button" tabindex="0" aria-expanded="true" aria-label="${esc(label)}">
          ${selected?.image ? `<img class="ski-set__trigger-thumb" src="${esc(selected.image)}" alt="${esc(selected.title)}" loading="lazy">` : ''}
          <span class="ski-set__trigger-label">${selected ? esc(selected.title) : esc(label)}</span>
          ${this.renderChevron('down')}
        </div>
        <div class="ski-set__popup">
          ${optionsHtml}
        </div>
      </div>${wrapEnd}`;
  }

  renderChevron(direction = 'left') {
    const deg = direction === 'right' ? 180 : direction === 'down' ? -90 : direction === 'up' ? 90 : 0;
    return `<svg class="ski-set__chevron" style="transform:rotate(${deg}deg)" xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none" aria-hidden="true">
      <path d="M5.16406 0.353516L0.70692 4.81066L5.16406 9.2678" stroke="currentColor"/>
    </svg>`;
  }

  renderInfoIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <path d="M4.548 7.332V3.468H5.34V7.332H4.548ZM4.944 2.916C4.784 2.916 4.664 2.876 4.584 2.796C4.504 2.716 4.464 2.6 4.464 2.448C4.464 2.288 4.504 2.172 4.584 2.1C4.664 2.02 4.784 1.98 4.944 1.98C5.072 1.98 5.18 2.02 5.268 2.1C5.364 2.172 5.412 2.288 5.412 2.448C5.412 2.608 5.364 2.728 5.268 2.808C5.18 2.88 5.072 2.916 4.944 2.916ZM4.944 9.888C4.264 9.888 3.624 9.76 3.024 9.504C2.424 9.248 1.896 8.896 1.44 8.448C0.992 7.992 0.64 7.464 0.384 6.864C0.128 6.264 0 5.624 0 4.944C0 4.256 0.128 3.616 0.384 3.024C0.64 2.424 0.992 1.9 1.44 1.452C1.896 0.996 2.424 0.64 3.024 0.384C3.624 0.128 4.264 0 4.944 0C5.632 0 6.272 0.128 6.864 0.384C7.464 0.64 7.988 0.996 8.436 1.452C8.892 1.9 9.248 2.424 9.504 3.024C9.76 3.616 9.888 4.256 9.888 4.944C9.888 5.624 9.76 6.264 9.504 6.864C9.248 7.464 8.892 7.992 8.436 8.448C7.988 8.896 7.464 9.248 6.864 9.504C6.272 9.76 5.632 9.888 4.944 9.888ZM4.944 9.216C5.536 9.216 6.088 9.108 6.6 8.892C7.12 8.668 7.576 8.36 7.968 7.968C8.36 7.576 8.664 7.124 8.88 6.612C9.104 6.092 9.216 5.536 9.216 4.944C9.216 4.352 9.104 3.8 8.88 3.288C8.664 2.768 8.36 2.312 7.968 1.92C7.576 1.528 7.12 1.224 6.6 1.008C6.088 0.784 5.536 0.672 4.944 0.672C4.352 0.672 3.796 0.784 3.276 1.008C2.764 1.224 2.312 1.528 1.92 1.92C1.528 2.312 1.22 2.768 0.996 3.288C0.78 3.8 0.672 4.352 0.672 4.944C0.672 5.536 0.78 6.092 0.996 6.612C1.22 7.124 1.528 7.576 1.92 7.968C2.312 8.36 2.764 8.668 3.276 8.892C3.796 9.108 4.352 9.216 4.944 9.216Z" fill="currentColor"/>
    </svg>`;
  }
}

if (!customElements.get('ski-set-selector')) {
  customElements.define('ski-set-selector', SkiSetSelectorElement);
}

})();
